import {defaultLevelConfig} from "../../config";
import {GameObjectType} from "../../config/types";
import {ShardCollected} from "../../types";
import {Vec2} from "../core/Vec2.js";

export class Player {
  public readonly id: string;
  public readonly ownerId: string;
  public readonly instanceId: number;
  public readonly radius = defaultLevelConfig.playerRadius;
  public readonly currentPosition = Vec2.zero();
  public readonly previousPosition = Vec2.zero();
  public readonly velocity = Vec2.zero();

  public collectedShards = new Set<number>();
  public collectedShardsWithSteps: ShardCollected[] = [];
  public isDead = false;
  public isCompleted = false;
  public attractionActive = false;
  public isActive = false;

  constructor(ownerId: string, instanceId: number = 0) {
    this.id = `${ownerId}-${instanceId}`;
    this.ownerId = ownerId;
    this.instanceId = instanceId;
  }

  /**
   * Activates the player, called upon their first input.
   * This is a one-way state change.
   */
  public activate(): void {
    this.isActive = true;
  }

  public updatePosition(
    gravity: Vec2,
    stepSize: number,
    dampingFactor: number,
  ): void {
    this.previousPosition.copyFrom(this.currentPosition);
    this.velocity.add(gravity.clone().mul(stepSize));

    const speed = this.velocity.length();
    const nonLinearDamping = dampingFactor * speed;
    this.velocity.mul(1 - nonLinearDamping * stepSize).finalize();

    this.currentPosition.add(this.velocity.clone().mul(stepSize)).finalize();
  }

  public addImpulse(impulse: Vec2, type: GameObjectType): void {
    this.velocity.add(impulse).finalize();
    if (type === GameObjectType.ATTRACTOR) {
      this.attractionActive = true;
    }
  }

  public reset(spawnPosition: Vec2): void {
    this.currentPosition.copyFrom(spawnPosition);
    this.previousPosition.copyFrom(this.currentPosition);
    this.velocity.set(0, 0);
    this.collectedShards.clear();
    this.collectedShardsWithSteps = [];
    this.isDead = false;
    this.isCompleted = false;
    this.attractionActive = false;
    this.isActive = false;
  }

  /**
   * Sets the player's state to "dead". This is a terminal state for the round.
   * The SimulationManager will stop applying physics to this player.
   */
  public markAsDead(): void {
    this.isDead = true;
    this.velocity.set(0, 0);
  }

  /**
   * Sets the player's state to "completed".
   * This is a terminal state for the round.
   * The SimulationManager will stop applying physics to this player.
   */
  public markAsCompleted(): void {
    this.isCompleted = true;
  }

  /**
   * Records that a shard was collected by this player.
   * The player itself doesn't know if this completes the level.
   * @param shardId The ID of the shard collected.
   * @param currentStep The simulation step of the collision.
   * @param timeOfImpact A value from 0-1 representing the sub-step time.
   */
  public recordShardCollection(
    shardId: number,
    currentStep: number,
    timeOfImpact: number,
  ): void {
    if (this.collectedShards.has(shardId)) return;

    this.collectedShards.add(shardId);
    this.collectedShardsWithSteps.push({
      shardId,
      steps: currentStep + timeOfImpact,
    });
  }
}
